import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from '../components/charts/ChartCard';
import ChartTooltip from '../components/charts/ChartTooltip';
import { FilterGroup, FilterSelect } from '../components/charts/FilterControls';
import StatCard from '../components/charts/StatCard';
import {
  CHART_COLORS,
  CHART_MARGIN,
  SERIES_COLORS,
  Y_AXIS_WIDTH,
  formatMs,
  formatPeriodLabel,
  formatPct,
  formatUsd,
  formatUsdAxis,
} from '../components/charts/chartUtils';
import DashboardLayout from '../components/DashboardLayout';
import SetupBanner from '../components/SetupBanner';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const TIMEFRAMES = [
  { id: '1h', label: '1h' },
  { id: 'day', label: '24h' },
  { id: 'week', label: '7d' },
  { id: 'month', label: '30d' },
  { id: 'year', label: '1y' },
];

const TIMEFRAME_LABELS = { '1h': '1h', day: '24h', week: '7d', month: '30d', year: '365d' };
const PIE_COLORS = [CHART_COLORS.routed, CHART_COLORS.cache_hit, CHART_COLORS.passthrough];

export default function HomePage() {
  const { selectedHash } = useProject();
  const [data, setData] = useState(null);
  const [timeframe, setTimeframe] = useState('month');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');

  useEffect(() => {
    if (!selectedHash) return;
    dashboardService.summary(
      selectedHash,
      { timeframe, api_key: apiKey, model },
      (res) => setData(res.data),
      console.error,
    );
  }, [selectedHash, timeframe, apiKey, model]);

  const chartData = useMemo(() => {
    if (!data?.series?.length) return [];
    return data.series.map((row) => ({
      period: row.period,
      label: formatPeriodLabel(row.period, data.segment),
      routed: row.requests.routed,
      cache_hit: row.requests.cache_hit,
      passthrough: row.requests.passthrough,
      routed_cost: row.cost.routed,
      passthrough_cost: row.cost.passthrough,
      routed_savings: row.savings.routed,
      cached_savings: row.savings.cached,
      total_requests: row.requests.routed + row.requests.cache_hit + row.requests.passthrough,
    }));
  }, [data]);

  const savingsPctChart = useMemo(
    () => (data?.savings_pct_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      savings_pct: row.savings_pct,
      billed: row.billed || 0,
    })),
    [data],
  );

  const passthroughChart = useMemo(
    () => (data?.passthrough_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      count: row.count,
      estimated_cost: row.estimated_cost == null ? null : Number(row.estimated_cost),
    })),
    [data],
  );

  const passthroughByModel = useMemo(
    () => (data?.passthrough_by_model || []).map((m) => ({
      model: m.model.length > 24 ? `${m.model.slice(0, 22)}…` : m.model,
      fullModel: m.model,
      count: m.count,
    })),
    [data],
  );

  const passthroughByModelTimeline = useMemo(() => {
    const models = data?.passthrough_by_model_series?.models || [];
    const series = data?.passthrough_by_model_series?.series || [];
    return {
      models,
      chartData: series.map((row) => ({
        label: formatPeriodLabel(row.period, data?.segment),
        ...Object.fromEntries(models.map((m) => [m, row[m] ?? null])),
      })),
    };
  }, [data]);

  const latencyChart = useMemo(
    () => (data?.latency_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      overhead_ms: row.latency_ms,
      generation_ms: row.generation_ms,
    })),
    [data],
  );

  const cacheRateChart = useMemo(
    () => (data?.cache_rate_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      rate: row.rate,
    })),
    [data],
  );

  const optimizationChart = useMemo(
    () => (data?.optimization_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      rate: row.rate,
    })),
    [data],
  );

  const evalCostChart = useMemo(
    () => (data?.eval_cost_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      cost: row.cost,
      count: row.count,
    })),
    [data],
  );

  const evalQualityChart = useMemo(
    () => (data?.eval_quality_series || []).map((row) => ({
      label: formatPeriodLabel(row.period, data?.segment),
      score: row.score,
    })),
    [data],
  );

  const routingPie = useMemo(() => {
    if (!data) return [];
    const total = data.request_count || 0;
    if (!total) return [];
    const routed = Math.round((data.routed_rate / 100) * total);
    const cached = Math.round((data.cache_rate / 100) * total);
    const passthrough = total - routed - cached;
    return [
      { name: 'Routed', value: routed },
      { name: 'Cached', value: cached },
      { name: 'Passthrough', value: Math.max(passthrough, 0) },
    ].filter((d) => d.value > 0);
  }, [data]);

  const byModelCost = useMemo(
    () => (data?.by_model || []).slice(0, 10).map((m) => ({
      model: m.model.length > 24 ? `${m.model.slice(0, 22)}…` : m.model,
      fullModel: m.model,
      cost: m.cost_usd,
      savings: m.savings_usd,
      requests: m.requests,
    })),
    [data],
  );

  const options = data?.filter_options || {};
  const tfLabel = TIMEFRAME_LABELS[timeframe] || timeframe;
  const hasCharts = chartData.length > 0;

  return (
    <DashboardLayout title="Dashboard">
      <SetupBanner />

      <div className="flex flex-wrap items-center gap-4 mb-5">
        <FilterGroup
          label="Timeframe"
          options={TIMEFRAMES}
          value={timeframe}
          onChange={setTimeframe}
        />
        <FilterSelect
          label="API key"
          value={apiKey}
          onChange={setApiKey}
          options={(options.keys || []).map((k) => ({ value: k.hash, label: k.name }))}
        />
        <FilterSelect
          label="Model"
          value={model}
          onChange={setModel}
          options={(options.models || []).map((m) => ({ value: m, label: m }))}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label={`Requests (${tfLabel})`}
          value={(data?.request_count || 0).toLocaleString()}
          hint={`${formatPct(data?.routed_rate)} routed · ${formatPct(data?.cache_rate)} cached · ${formatPct(Math.max(0, 100 - (data?.optimization_rate || 0)))} passthrough`}
          accent
        />
        <StatCard
          label={`Spend (${tfLabel})`}
          value={formatUsd(data?.actual_cost_usd)}
          hint={`Originally ${formatUsd(data?.original_cost_usd)} · saved ${formatUsd(data?.savings_usd)}`}
        />
        <StatCard
          label="Savings"
          value={formatPct(data?.savings_rate)}
          hint={`${formatUsd(data?.savings_usd)} saved this period`}
        />
        <StatCard
          label="Quality"
          value={data?.eval_avg_score != null ? `${data.eval_avg_score}/100` : '—'}
          hint={data?.eval_cost_usd ? `${formatUsd(data.eval_cost_usd)} eval cost` : 'No evals run yet'}
        />
      </div>

      {hasCharts ? (
        <div className="space-y-4">
          <SectionTitle>Traffic & cost</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Requests" subtitle="Routed, cached, passthrough">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.count} allowDecimals={false} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={(v) => v.toLocaleString()} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="routed" stackId="r" fill={CHART_COLORS.routed} name="Routed" />
                  <Bar dataKey="cache_hit" stackId="r" fill={CHART_COLORS.cache_hit} name="Cached" />
                  <Bar dataKey="passthrough" stackId="r" fill={CHART_COLORS.passthrough} name="Passthrough" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Cost" subtitle="Routed vs provider (passthrough)">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} interval="preserveStartEnd" />
                  <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="routed_cost" stackId="c" fill={CHART_COLORS.routed} name="Routed" />
                  <Bar dataKey="passthrough_cost" stackId="c" fill={CHART_COLORS.passthrough} name="Provider" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Savings" subtitle="Routed vs cached savings">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} interval="preserveStartEnd" />
                  <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="routed_savings" stackId="s" fill={CHART_COLORS.routed} name="Routed" />
                  <Bar dataKey="cached_savings" stackId="s" fill={CHART_COLORS.cached} name="Cached" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <SectionTitle>Performance</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ChartCard title="Savings %" subtitle="Savings as % of original cost">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={savingsPctChart} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_COLORS.savings} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={CHART_COLORS.savings} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.pct} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatPct} />} />
                  <Area type="monotone" dataKey="savings_pct" stroke={CHART_COLORS.savings} fill="url(#savingsGrad)" name="Savings %" connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Cache hit rate" subtitle="Over time">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cacheRateChart} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.pct} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatPct} />} />
                  <Line type="monotone" dataKey="rate" stroke={CHART_COLORS.cache_hit} strokeWidth={2} dot={false} name="Cache %" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Optimization" subtitle="% routed or cached">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={optimizationChart} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.pct} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatPct} />} />
                  <Line type="monotone" dataKey="rate" stroke={CHART_COLORS.routed} strokeWidth={2} dot={false} name="Optimized %" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Request mix">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={routingPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                    {routingPie.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <SectionTitle>Latency</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Avg latency" subtitle={`${formatMs(data?.latency_avg_ms ?? 0)} end-to-end`} tall>
              <div className="h-full flex flex-col items-center justify-center">
                <div className="text-5xl font-bold text-theme-primary tabular-nums">
                  {formatMs(data?.latency_avg_ms ?? 0)}
                </div>
                <p className="text-sm opacity-50 mt-3">Mean end-to-end latency ({tfLabel})</p>
                <div className="grid grid-cols-2 gap-4 mt-6 w-full max-w-xs text-center">
                  <div>
                    <div className="text-lg font-semibold text-theme-primary">{formatMs(data?.latency_overhead_avg_ms ?? 0)}</div>
                    <div className="text-xs opacity-50">Our overhead</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold" style={{ color: CHART_COLORS.generation }}>{formatMs(data?.generation_avg_ms ?? 0)}</div>
                    <div className="text-xs opacity-50">Provider</div>
                  </div>
                </div>
              </div>
            </ChartCard>

            <ChartCard title="Our latency" subtitle="Router overhead over time" tall>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyChart} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis tickFormatter={formatMs} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.ms} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatMs} />} />
                  <Line type="monotone" dataKey="overhead_ms" stroke={CHART_COLORS.overhead} strokeWidth={2} dot={false} name="Our latency" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Generation time" subtitle="External provider over time" tall>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyChart} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis tickFormatter={formatMs} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.ms} opacity={0.5} />
                  <Tooltip content={<ChartTooltip valueFormatter={formatMs} />} />
                  <Line type="monotone" dataKey="generation_ms" stroke={CHART_COLORS.generation} strokeWidth={2} dot={false} name="Provider" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <SectionTitle>Passthrough</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Passthrough" subtitle="Requests not routed or cached" tall>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={passthroughChart} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.count} opacity={0.5} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="count" fill={CHART_COLORS.passthrough} name="Requests" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="right" dataKey="estimated_cost" fill={CHART_COLORS.cost} name="Est. cost" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Passthrough by model" subtitle="Which models skip routing" tall>
              {passthroughByModel.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={passthroughByModel} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} opacity={0.5} />
                    <YAxis type="category" dataKey="model" tick={{ fontSize: 9 }} width={100} opacity={0.7} />
                    <Tooltip content={<ChartTooltip valueFormatter={(v) => v.toLocaleString()} />} />
                    <Bar dataKey="count" fill={CHART_COLORS.passthrough} name="Requests" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartHint>No passthrough requests in this period</EmptyChartHint>
              )}
            </ChartCard>

            <ChartCard title="Passthrough timeline" subtitle="By model over time" tall>
              {passthroughByModelTimeline.chartData.length > 0 && passthroughByModelTimeline.models.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={passthroughByModelTimeline.chartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.count} allowDecimals={false} opacity={0.5} />
                    <Tooltip content={<ChartTooltip valueFormatter={(v) => v.toLocaleString()} />} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {passthroughByModelTimeline.models.map((model, i) => (
                      <Bar
                        key={model}
                        dataKey={model}
                        stackId="pt"
                        fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                        name={model.length > 20 ? `${model.slice(0, 18)}…` : model}
                        radius={i === passthroughByModelTimeline.models.length - 1 ? [3, 3, 0, 0] : undefined}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartHint>No passthrough requests in this period</EmptyChartHint>
              )}
            </ChartCard>
          </div>

          {(evalCostChart.length > 0 || evalQualityChart.length > 0) && (
            <>
              <SectionTitle>Evals</SectionTitle>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {evalCostChart.length > 0 && (
                  <ChartCard title="Eval cost" subtitle="Cost of running quality evals" tall>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={evalCostChart} margin={CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                        <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                        <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                        <Bar dataKey="cost" fill={CHART_COLORS.eval} name="Eval cost" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
                {evalQualityChart.length > 0 && (
                  <ChartCard title="Eval quality" subtitle="Avg routed model score" tall>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={evalQualityChart} margin={CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.pct} opacity={0.5} />
                        <Tooltip content={<ChartTooltip valueFormatter={(v) => (v == null ? '—' : `${v}/100`)} />} />
                        <Line type="monotone" dataKey="score" stroke={CHART_COLORS.eval} strokeWidth={2} dot={{ r: 3 }} name="Score" connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>
            </>
          )}

          {byModelCost.length > 0 && (
            <>
              <SectionTitle>By model</SectionTitle>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartCard title="Cost by model" subtitle="Top models by spend" tall>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byModelCost} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} opacity={0.5} />
                      <YAxis type="category" dataKey="model" tick={{ fontSize: 9 }} width={100} opacity={0.7} />
                      <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                      <Bar dataKey="cost" fill={CHART_COLORS.cost} name="Cost" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Savings by model" subtitle="Top models by savings" tall>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byModelCost} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} opacity={0.5} />
                      <YAxis type="category" dataKey="model" tick={{ fontSize: 9 }} width={100} opacity={0.7} />
                      <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                      <Bar dataKey="savings" fill={CHART_COLORS.savings} name="Savings" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              <div className="rounded-2xl border border-theme-secondary overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-theme-secondary bg-theme-secondary/30 text-left text-xs uppercase tracking-wide opacity-60">
                      <th className="px-4 py-3">Model</th>
                      <th className="px-4 py-3">Requests</th>
                      <th className="px-4 py-3">Cost</th>
                      <th className="px-4 py-3">Savings</th>
                      <th className="px-4 py-3">Routed</th>
                      <th className="px-4 py-3">Cache</th>
                      <th className="px-4 py-3">Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.by_model || []).map((m) => (
                      <tr key={m.model} className="border-b border-theme-secondary/50 hover:bg-theme-secondary/20">
                        <td className="px-4 py-2.5 font-medium truncate max-w-[200px]" title={m.model}>{m.model}</td>
                        <td className="px-4 py-2.5 tabular-nums">{m.requests.toLocaleString()}</td>
                        <td className="px-4 py-2.5 tabular-nums">{formatUsd(m.cost_usd)}</td>
                        <td className="px-4 py-2.5 tabular-nums text-emerald-500">{formatUsd(m.savings_usd)}</td>
                        <td className="px-4 py-2.5 tabular-nums">{formatPct(m.routed_rate)}</td>
                        <td className="px-4 py-2.5 tabular-nums">{formatPct(m.cache_rate)}</td>
                        <td className="px-4 py-2.5 tabular-nums">{formatMs(m.avg_latency_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        data && (
          <div className="rounded-2xl border border-dashed border-theme-secondary p-16 text-center opacity-60">
            <p className="text-lg mb-1">No traffic in this period</p>
            <p className="text-sm">Send requests through os-router to see analytics here.</p>
          </div>
        )
      )}
    </DashboardLayout>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-widest opacity-40 pt-2">{children}</h2>
  );
}

function EmptyChartHint({ children }) {
  return (
    <div className="h-full flex items-center justify-center text-sm opacity-50 text-center px-4">
      {children}
    </div>
  );
}
